import { IPublicClientApplication } from "@azure/msal-browser";
import { apiLoginRequest } from "../Config";
import ResourceModel from "../Models/ResourceModel";
import ResourceRentModel from "../Models/ResourceRentModel";

export default class ResourcesService {
  msalInstance: IPublicClientApplication;

  constructor(msalInstance: IPublicClientApplication) {
    this.msalInstance = msalInstance;
  }

  public async get(): Promise<ResourceModel[]> {
    const baseUrl = process.env.REACT_APP_API_URL;
    const accessToken = await this.acquireAccessToken();

    const data = await fetch(`${baseUrl}/api/Resource`, {
      method: "get",
      headers: new Headers({
        Authorization: "Bearer " + accessToken,
      }),
    }).then(function (response) {
      return response.json();
    });

    return data;
  }

  public async upsert(data: ResourceModel) {
    const baseUrl = process.env.REACT_APP_API_URL;
    const accessToken = await this.acquireAccessToken();

    const responseData = await fetch(`${baseUrl}/api/Resource`, {
      method: "post",
      headers: new Headers({
        Authorization: "Bearer " + accessToken,
      }),
      body: JSON.stringify(data),
    }).then(function (response) {
      return response.json();
    });

    if (responseData.id && data.image) {
      const formData = new FormData();
      formData.append("file", data.image);
      formData.append("fileName", data.image.name);
      await fetch(`${baseUrl}/api/ResourceImage?id=${responseData.id}`, {
        method: "post",
        headers: new Headers({
          Authorization: "Bearer " + accessToken,
        }),
        body: formData,
      });
    }
  }

  public async getImage(id: number): Promise<File | undefined> {
    const baseUrl = process.env.REACT_APP_API_URL;
    const accessToken = await this.acquireAccessToken();

    const data = await fetch(`${baseUrl}/api/ResourceImage?id=${id}`, {
      method: "get",
      headers: new Headers({
        Authorization: "Bearer " + accessToken,
      }),
    })
      .then((response) => {
        if (response.status >= 400 && response.status < 600) {
          throw new Error("Bad response from server");
        }
        return response;
      })
      .then((returnedResponse) => {
        return returnedResponse;
      })
      .catch((error) => {
        // Your error is here!
        console.log(error);
        throw error;
      });

    const blob = await data.blob();
    const filename = data.headers.get("filename") ?? "";

    if (blob.size === 0) return undefined;

    return new File([blob], filename);
  }

  public async getRents(resourceId?: number): Promise<ResourceRentModel[]> {
    const baseUrl = process.env.REACT_APP_API_URL;
    const accessToken = await this.acquireAccessToken();

    const data = await fetch(
      `${baseUrl}/api/ResourceRent${resourceId !== undefined ? "?resourceid=" + resourceId : ""}`,
      {
        method: "get",
        headers: new Headers({
          Authorization: "Bearer " + accessToken,
        }),
      },
    ).then(function (response) {
      return response.json();
    });

    return data;
  }

  public async upsertRent(data: ResourceRentModel): Promise<string> {
    const baseUrl = process.env.REACT_APP_API_URL;
    const accessToken = await this.acquireAccessToken();

    const responseData = await fetch(`${baseUrl}/api/ResourceRent`, {
      method: "post",
      headers: new Headers({
        Authorization: "Bearer " + accessToken,
      }),
      body: JSON.stringify(data),
    }).then(function (response) {
      return response.json();
    });

    return responseData?.error;
  }

  public async saveReturnedRent(resourceId: number) {
    const baseUrl = process.env.REACT_APP_API_URL;
    const accessToken = await this.acquireAccessToken();

    const responseData = await fetch(
      `${baseUrl}/api/ResourceRentReturned?resourceId=${resourceId}`,
      {
        method: "post",
        headers: new Headers({
          Authorization: "Bearer " + accessToken,
        }),
      },
    ).then(function (response) {
      return response.json();
    });
  }

  private async acquireAccessToken() {
    const account = this.msalInstance.getActiveAccount();
    if (!account) {
      throw Error(
        "No active account! Verify a user has been signed in and setActiveAccount has been called.",
      );
    }

    const authResult = await this.msalInstance.acquireTokenSilent({
      ...apiLoginRequest,
      account: account,
    });

    return authResult.accessToken;
  }
}
